import Store from './Store';

let currentOrderNo = '';

/**
 * 安全读取 sessionStorage 中的小程序数据。
 * @param {string} key - sessionStorage 缓存 key。
 * @returns {Object|null} 小程序数据。
 */
function getSessionAppData(key) {
  try {
    return Store.getSession(key) || null;
  } catch (error) {
    return null;
  }
}

/**
 * 获取当前可用于 Sentry 的稳定用户标识。
 * @param {Object} [options] - 外部传入的用户标识。
 * @param {string|number} [options.userId] - 登录用户 ID。
 * @param {string|number} [options.deviceId] - 设备 ID。
 * @param {string|number} [options.uid] - 本地兜底 UID。
 * @returns {string} Sentry user.id。
 */
function resolveSentryUserId(options = {}) {
  return String(
    options.userId ||
      Store.get('__uid') ||
      options.deviceId ||
      Store.get('deviceId') ||
      options.uid ||
      Store.get('uid') ||
      ''
  );
}

/**
 * 获取当前可用于 Sentry 的订单号。
 * @param {string|number} [orderNo] - 外部传入的订单号。
 * @returns {string} 订单号。
 */
function resolveSentryOrderNo(orderNo) {
  const wxData = window._wxData || getSessionAppData('wxData') || {};
  const dyData = window._dyData || getSessionAppData('dyData') || {};
  return String(
    orderNo ||
      window.__orderNo__ ||
      wxData.orderNo ||
      dyData.orderNo ||
      currentOrderNo ||
      ''
  );
}

/**
 * 设置 Sentry 用户标识，仅上报稳定 ID，不上传昵称、手机号等隐私信息。
 * @param {Object} [options] - 外部传入的用户标识。
 * @param {string|number} [options.userId] - 登录用户 ID。
 * @param {string|number} [options.deviceId] - 设备 ID。
 * @param {string|number} [options.uid] - 本地兜底 UID。
 * @returns {void}
 */
export function setSentryUser(options = {}) {
  const id = resolveSentryUserId(options);
  // Sentry 未初始化或用户标识为空时不设置，避免产生无效用户维度。
  if (!window._Sentry || !id) {
    return;
  }
  window._Sentry.setUser({ id });
}

/**
 * 设置 Sentry 订单上下文，只上报订单号用于搜索和定位。
 * @param {string|number} orderNo - 订单号。
 * @returns {void}
 */
export function setSentryOrderNo(orderNo) {
  currentOrderNo = resolveSentryOrderNo(orderNo);
  // Sentry 未初始化或订单号为空时不设置订单维度。
  if (!window._Sentry || !currentOrderNo) {
    return;
  }
  window._Sentry.configureScope((scope) => {
    scope.setTag('order_no', currentOrderNo);
    scope.setContext('order', {
      orderNo: currentOrderNo
    });
  });
}

/**
 * 初始化 Sentry 全局上下文，补齐用户标识和页面已有订单号。
 * @returns {void}
 */
export function initSentryScope() {
  setSentryUser();
  setSentryOrderNo();
}

/**
 * 手动上报已 catch 的业务错误，确保流程继续时仍能在 Sentry 中定位。
 * @param {Error} error - 需要上报的错误对象。
 * @param {Object} [options] - 上报选项。
 * @param {string} [options.name] - 业务错误名称。
 * @param {string} [options.level] - Sentry 错误等级。
 * @param {Object} [options.context] - 额外业务上下文。
 * @param {string|number} [options.orderNo] - 本次错误关联的订单号。
 * @returns {void}
 */
export function captureBusinessError(error, options = {}) {
  setSentryUser();
  // 只上报 Error 对象，字符串日志继续留在本地 logger，避免制造 Sentry 噪音。
  if (!window._Sentry || !(error instanceof Error)) {
    return;
  }
  const orderNo = resolveSentryOrderNo(options.orderNo);
  window._Sentry.withScope((scope) => {
    scope.setTag('business_error', 'true');
    scope.setTag('handled', 'true');
    scope.setLevel(options.level || 'error');
    // 有订单号时同时写入 tag 和 context，兼顾搜索与详情查看。
    if (orderNo) {
      scope.setTag('order_no', orderNo);
      scope.setContext('order', {
        orderNo
      });
    }
    // 有业务错误名称时写入 tag，方便按类型聚合筛选。
    if (options.name) {
      scope.setTag('business_error_name', options.name);
    }
    // 有额外上下文时写入 business context，避免污染顶层字段。
    if (options.context) {
      scope.setContext('business', options.context);
    }
    window._Sentry.captureException(error);
  });
}
